import React from 'react';
import { motion } from 'framer-motion';
import { Check, Heart, ExternalLink } from 'lucide-react';
import { PageTransition } from '../components/PageTransition';
import { Card } from '../components/ui/Card';
const paymentMethods = [{
  name: 'Venmo',
  handle: '@threedovespodcast',
  qrImage: 'https://cdn.magicpatterns.com/uploads/sFMnfoUFW31LjM11iTc9HE/venmo.jpg',
  url: 'https://venmo.com/threedovespodcast',
  color: '#008CFF'
}, {
  name: 'Cash App',
  handle: '',
  qrImage: 'https://cdn.magicpatterns.com/uploads/8CSpoX9KqzjAg2qpDX5DCC/cashapp.jpg',
  url: '',
  color: '#00D632'
}];
export function DonatePage() {
  return <PageTransition data-id="element-168">
      <div className="pt-24 pb-16 min-h-screen" data-id="element-169">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-170">
          {/* Header */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-16" data-id="element-171">
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-172">
              Support the Mission
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed" data-id="element-173">
              Your generosity fuels honest conversations, real stories, and
              tangible outreach to those in need.
            </p>
          </motion.div>

          {/* How to Give Instructions */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="max-w-2xl mx-auto text-center mb-12 bg-dark-800 rounded-xl p-6 border border-dark-600" data-id="element-174">
            <h3 className="text-lg font-serif font-bold text-white mb-3" data-id="element-175">
              How to Give
            </h3>
            <p className="text-white/70 leading-relaxed" data-id="element-176">
              <span className="text-gold-400 font-medium" data-id="element-177">Venmo:</span> On your
              phone, open Venmo and scan the QR code to give. On a computer,
              click the link below to log in to your Venmo account and give.
              <br data-id="element-178" />
              <br data-id="element-179" />
              <span className="text-gold-400 font-medium" data-id="element-180">Cash App:</span> Open
              Cash App on your phone and scan the QR code to give. Cash App
              donations must be made by scanning with your phone.
            </p>
          </motion.div>

          {/* QR Code Cards */}
          <div className="flex flex-col md:flex-row justify-center items-stretch gap-8 mb-16" data-id="element-181">
            {paymentMethods.map((method, index) => {
            const cardContent = <Card className="h-full bg-dark-700 border-dark-600 overflow-hidden transition-all duration-300 group-hover:border-gold-500/50 group-hover:shadow-lg group-hover:shadow-gold-500/10" data-id="element-182">
                  <div className="p-6 flex flex-col items-center text-center" data-id="element-183">
                    {/* Payment Method Name */}
                    <div className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold mb-6" style={{
                  backgroundColor: `${method.color}20`,
                  color: method.color
                }} data-id="element-184">
                      {method.name}
                    </div>

                    {/* QR Code Image */}
                    <div className="w-full max-w-[280px] aspect-square rounded-xl overflow-hidden bg-white p-3 mb-6 group-hover:scale-[1.02] transition-transform duration-300" data-id="element-185">
                      <img src={method.qrImage} alt={`${method.name} QR Code`} className="w-full h-full object-contain rounded-lg" data-id="element-186" />
                    </div>

                    {/* Handle / Info */}
                    {method.handle && <p className="text-white/60 text-sm mb-4" data-id="element-187">
                        {method.handle}
                      </p>}

                    {/* CTA */}
                    {method.url ? <div className="w-full py-3 rounded-lg font-medium text-white transition-all duration-300 group-hover:opacity-90" style={{
                  backgroundColor: method.color
                }} data-id="element-188">
                        Give via {method.name}
                      </div> : <div className="w-full py-3 rounded-lg font-medium text-white/70 bg-dark-600 text-center" data-id="element-189">
                        Scan QR Code with {method.name}
                      </div>}
                  </div>
                </Card>;
            return <motion.div key={method.name} initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.15
            }} className="w-full md:w-[360px]" data-id="element-190">
                  {method.url ? <a href={method.url} target="_blank" rel="noopener noreferrer" className="block group" data-id="element-191">
                      {cardContent}
                    </a> : <div className="block" data-id="element-192">{cardContent}</div>}
                </motion.div>;
          })}
          </div>

          {/* Where Your Money Goes */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-dark-800 rounded-2xl p-8 md:p-12 border border-dark-600" data-id="element-193">
            <div className="max-w-3xl mx-auto" data-id="element-194">
              <div className="flex items-center justify-center mb-8" data-id="element-195">
                <div className="w-12 h-12 rounded-full bg-gold-500/10 flex items-center justify-center mr-4" data-id="element-196">
                  <Heart className="w-6 h-6 text-gold-500" data-id="element-197" />
                </div>
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-white" data-id="element-198">
                  Where Your Money Goes
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-199">
                {['Podcast Production & Equipment', 'Essential Bags Outreach Program', 'Guest Travel & Support', 'Bookstore & Educational Resources'].map((item, i) => <div key={i} className="flex items-center p-4 bg-dark-700 rounded-xl border border-dark-600" data-id="element-200">
                    <div className="w-6 h-6 rounded-full bg-gold-500/20 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-201">
                      <Check className="w-4 h-4 text-gold-500" data-id="element-202" />
                    </div>
                    <span className="text-white/80" data-id="element-203">{item}</span>
                  </div>)}
              </div>

              <p className="text-center text-white/50 text-sm italic mt-8" data-id="element-204">
                3 Doves Podcast is committed to financial transparency. Every
                dollar is used to further the mission of sharing the Gospel and
                serving our community. A 20% administration fee is applied to
                all donations. Please note: 3 Doves Podcast is not a registered
                non-profit organization. Contributions are not tax-deductible.
              </p>
            </div>
          </motion.div>

          {/* How to Pray for the Ministry */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-12 bg-dark-800 rounded-2xl p-8 md:p-12 border border-dark-600" data-id="element-205">
            <div className="max-w-3xl mx-auto" data-id="element-206">
              <div className="text-center mb-8" data-id="element-207">
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3" data-id="element-208">
                  Pray for the Ministry
                </h2>
                <p className="text-white/60" data-id="element-209">
                  Your prayers mean just as much as your giving. Here's how you
                  can lift up 3 Doves Podcast in prayer:
                </p>
              </div>

              <ol className="space-y-4" data-id="element-210">
                {['That many will hear the good news of Jesus Christ.', 'Humbleness will always be our banner.', 'Christ will always be our center.', 'Resources will always be available.', 'Jason will continue to meet more and more people that want to share what Jesus has done in and through them.'].map((item, i) => <li key={i} className="flex items-start p-4 bg-dark-700 rounded-xl border border-dark-600" data-id="element-211">
                    <span className="w-7 h-7 rounded-full bg-gold-500/20 flex items-center justify-center mr-4 flex-shrink-0 mt-0.5" data-id="element-212">
                      <span className="text-sm font-bold text-gold-500" data-id="element-213">
                        {i + 1}
                      </span>
                    </span>
                    <span className="text-white/80 leading-relaxed" data-id="element-214">
                      {item}
                    </span>
                  </li>)}
              </ol>
            </div>
          </motion.div>

          {/* Sponsors */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mt-12 bg-dark-800 rounded-2xl p-8 md:p-12 border border-dark-600" data-id="element-215">
            <div className="max-w-3xl mx-auto" data-id="element-216">
              <div className="text-center mb-8" data-id="element-217">
                <h2 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3" data-id="element-218">
                  Our Sponsors
                </h2>
                <p className="text-white/60" data-id="element-219">
                  We're grateful for the businesses that support the mission of
                  3 Doves Podcast.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-220">
                {[{
                name: "Horseman's Landscape",
                url: 'https://horsemanslandscape.com'
              }, {
                name: 'First Response Water & Mold Removal',
                url: 'https://firstresponsewaterandmold.com/'
              }, {
                name: 'Chill of Cape Coral',
                url: 'https://www.capechill.net/'
              }].map((sponsor, i) => <a key={i} href={sponsor.url} target="_blank" rel="noopener noreferrer" className="group" data-id="element-221">
                    <div className="flex flex-col items-center justify-center p-6 bg-dark-700 rounded-xl border border-dark-600 hover:border-gold-500/50 transition-all duration-300 h-full text-center" data-id="element-222">
                      <h3 className="text-lg font-serif font-bold text-white mb-3 group-hover:text-gold-400 transition-colors" data-id="element-223">
                        {sponsor.name}
                      </h3>
                      <span className="flex items-center text-sm text-gold-500 gap-1" data-id="element-224">
                        <ExternalLink className="w-3.5 h-3.5" data-id="element-225" />
                        Visit Website
                      </span>
                    </div>
                  </a>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </PageTransition>;
}